<template>
  <StatsWidget
    :title="$t('stats_dashboard.installer_stats.title')"
    :description="$t('stats_dashboard.installer_stats.description')"
    icon="tool"
    tone="rose"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="hasInstallers"
    :span-full="true"
    @refresh="$emit('refresh')"
  >
    <div class="stats-installer-stats__scroll">
      <table class="stats-installer-stats__table">
        <thead>
          <tr>
            <th class="stats-installer-stats__th-name">
              {{ $t('stats_dashboard.installer_stats.headers.name') }}
            </th>
            <th class="stats-installer-stats__th-num">
              {{ $t('stats_dashboard.installer_stats.headers.work_orders') }}
            </th>
            <th class="stats-installer-stats__th-num">
              {{ $t('stats_dashboard.installer_stats.headers.total_value') }}
            </th>
            <th class="stats-installer-stats__th-num">
              {{ $t('stats_dashboard.installer_stats.headers.avg_per_day') }}
            </th>
            <th class="stats-installer-stats__th-num">
              {{ $t('stats_dashboard.installer_stats.headers.hours') }}
            </th>
            <th class="stats-installer-stats__th-num">
              {{ $t('stats_dashboard.installer_stats.headers.days') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="installer in installers"
            :key="installer.user_id"
            class="stats-installer-stats__row"
          >
            <td class="stats-installer-stats__td-name">
              <span class="stats-installer-stats__name">
                {{ installer.name }}
              </span>
            </td>
            <td class="stats-installer-stats__td-num">
              {{ formatInteger(installer.work_orders_completed) }}
            </td>
            <td class="stats-installer-stats__td-num stats-installer-stats__td-num--strong">
              {{ formatCurrency(installer.total_invoice_value) }}
            </td>
            <td class="stats-installer-stats__td-num">
              {{ formatCurrency(installer.avg_invoice_value_per_day) }}
            </td>
            <td class="stats-installer-stats__td-num">
              {{ formatHours(installer.logged_hours) }}
            </td>
            <td class="stats-installer-stats__td-num">
              {{ formatInteger(installer.days_worked) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </StatsWidget>
</template>

<script>
  /**
   * Per-installer per-day stats (§10). The heaviest widget on the
   * dashboard — backend has an 8s timeout in the combined endpoint, so
   * a missing payload here is expected to surface as an error state on
   * busy orgs. We render a horizontally-scrolling table on small
   * screens rather than collapsing columns; sortable columns are a
   * Phase-2 improvement.
   */
  import { computed } from 'vue';
  import StatsWidget from './StatsWidget.vue';
  import {
    formatCurrency,
    formatHours,
    formatInteger,
  } from '@/utils/statsFormat';

  export default {
    name: 'StatsInstallerStats',

    components: { StatsWidget },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const installers = computed(() => props.widget.data?.installers || []);
      const hasInstallers = computed(() => installers.value.length > 0);

      return {
        installers,
        hasInstallers,
        formatCurrency,
        formatHours,
        formatInteger,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-installer-stats {
    &__scroll {
      overflow-x: auto;
      // Subtle inset shadow so the user knows the table can scroll on
      // narrow viewports — without bringing in a scroll library.
      background:
        linear-gradient(to right, #fff, #fff) left center/20px 100% no-repeat,
        linear-gradient(to right, #fff, #fff) right center/20px 100% no-repeat,
        linear-gradient(to right, rgba(0, 0, 0, 0.06), transparent) left center/8px 100% no-repeat,
        linear-gradient(to left, rgba(0, 0, 0, 0.06), transparent) right center/8px 100% no-repeat;
      background-attachment: local, local, scroll, scroll;
    }

    &__table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.8125rem;

      th,
      td {
        padding: 0.625rem 0.75rem;
        text-align: left;
        border-bottom: 1px solid var(--slate-100);
        white-space: nowrap;
      }

      th {
        font-size: 0.6875rem;
        font-weight: 600;
        color: var(--slate-500);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        background: var(--slate-50);
        position: sticky;
        top: 0;
      }

      tbody tr {
        transition: background-color 0.15s ease;

        @include hover {
          background: var(--slate-50);
        }
      }
    }

    &__th-num,
    &__td-num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }

    &__td-num--strong {
      font-weight: 700;
      color: var(--slate-900);
    }

    &__td-name {
      font-weight: 500;
      color: var(--slate-800);
    }

    &__name {
      @include ellipsis(1);
      max-width: 18rem;
      display: inline-block;
    }
  }
</style>
